import React, { useEffect, useState, useCallback } from "react";
import { Shield, Loader2, Search, Send } from "lucide-react";
import { PageHeader } from "@/components/shell/TopBar";
import { useLaunch } from "@/context/LaunchContext";
import { adminListUsers, adminPatchUserPlan, adminPatchUserStatus, adminListTickets, adminPatchTicket, adminReplyTicket, PlanCode } from "@/api/launch";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

const S = (v: unknown) => (v == null ? "" : String(v));
const arr = (d: any): any[] => (Array.isArray(d) ? d : d?.users ?? d?.tickets ?? d?.items ?? d?.data ?? []);
const PLANS: PlanCode[] = ["FREE", "PRO", "ELITE", "PREMIUM"];
const STATUSES = ["OPEN", "WAITING", "RESOLVED", "CLOSED"];

export default function Admin() {
  const { lang } = useI18n();
  const { isAdmin } = useLaunch();
  const [tab, setTab] = useState<"users" | "tickets">("users");
  const [users, setUsers] = useState<any[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");

  const loadUsers = useCallback((query?: string) => {
    setLoading(true); setDenied(false);
    adminListUsers(query).then((d) => setUsers(arr(d)))
      .catch((e: any) => { if (e?.status === 401 || e?.status === 403) setDenied(true); setUsers([]); })
      .finally(() => setLoading(false));
  }, []);
  const loadTickets = useCallback(() => {
    setLoading(true);
    adminListTickets().then((d) => setTickets(arr(d))).catch(() => setTickets([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (tab === "users") loadUsers(); else loadTickets(); }, [tab, loadUsers, loadTickets]);

  if (!isAdmin || denied) {
    return (
      <div data-testid="admin-unauthorized" className="grid place-items-center py-24 text-center">
        <Shield className="mb-3 h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{lang === "tr" ? "Bu alana erişim yetkin yok." : "You don't have access to this area."}</p>
      </div>
    );
  }

  const uid = (u: any) => S(u?.id ?? u?.user_id);
  const tid = (t: any) => S(t?.id ?? t?.ticket_id);

  async function changePlan(u: any, value: PlanCode) {
    try { await adminPatchUserPlan(uid(u), value); toast.success("OK"); loadUsers(q); } catch { toast.error("—"); }
  }
  async function changeStatus(u: any, value: "ACTIVE" | "SUSPENDED") {
    try { await adminPatchUserStatus(uid(u), value); toast.success("OK"); loadUsers(q); } catch { toast.error("—"); }
  }
  async function setTicketStatus(t: any, value: string) {
    try { await adminPatchTicket(tid(t), { status: value }); toast.success("OK"); loadTickets(); } catch { toast.error("—"); }
  }
  async function sendReply(t: any) {
    if (!replyText.trim()) return;
    try { await adminReplyTicket(tid(t), replyText); setReplyText(""); setReplyFor(null); toast.success("OK"); } catch { toast.error("—"); }
  }

  return (
    <div data-testid="admin-page">
      <PageHeader title={lang === "tr" ? "Admin Merkezi" : "Admin Center"} subtitle={lang === "tr" ? "Kullanıcı ve destek yönetimi." : "User and support management."} />
      <div className="mb-5 flex gap-2">
        {(["users", "tickets"] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`rounded-full px-4 py-2 text-sm font-semibold transition ${tab === t ? "bg-atlas-500 text-atlas-ink" : "border border-white/12 text-muted-foreground"}`}>
            {t === "users" ? (lang === "tr" ? "Kullanıcılar" : "Users") : (lang === "tr" ? "Destek" : "Support")}
          </button>
        ))}
      </div>

      {loading && <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-atlas-400" /></div>}

      {!loading && tab === "users" && (
        <>
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadUsers(q)} data-testid="admin-user-search" placeholder={lang === "tr" ? "E-posta / isim ara…" : "Search email / name…"} className="w-full bg-transparent py-2.5 text-sm text-foreground outline-none" />
          </div>
          <div className="panel overflow-x-auto p-2" data-testid="admin-users-table">
            <table className="w-full min-w-[720px] text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-3">Email</th><th className="p-3">{lang === "tr" ? "İsim" : "Name"}</th><th className="p-3">Plan</th><th className="p-3">Trial</th><th className="p-3">Stripe</th><th className="p-3">{lang === "tr" ? "Durum" : "Status"}</th>
              </tr></thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={uid(u) || i} className="border-t border-line" data-testid={`admin-user-${i}`}>
                    <td className="p-3 text-foreground/90">{S(u?.email)}</td>
                    <td className="p-3 text-muted-foreground">{S(u?.full_name ?? u?.name)}</td>
                    <td className="p-3">
                      <select defaultValue={S(u?.plan ?? u?.effective_plan ?? u?.base_plan)} onChange={(e) => changePlan(u, e.target.value as PlanCode)} className="rounded-lg border border-white/10 bg-navy-900 px-2 py-1 text-xs text-foreground">
                        {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{u?.trial?.active ? (lang === "tr" ? "Aktif" : "Active") : (u?.trial?.consumed ? (lang === "tr" ? "Bitti" : "Used") : "—")}</td>
                    <td className="p-3 text-xs text-muted-foreground">{S(u?.stripe_subscription_status ?? u?.subscription_status) || "—"}</td>
                    <td className="p-3">
                      <select defaultValue={S(u?.status ?? u?.account_status) || "ACTIVE"} onChange={(e) => changeStatus(u, e.target.value as "ACTIVE" | "SUSPENDED")} className="rounded-lg border border-white/10 bg-navy-900 px-2 py-1 text-xs text-foreground">
                        <option value="ACTIVE">ACTIVE</option><option value="SUSPENDED">SUSPENDED</option>
                      </select>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">{lang === "tr" ? "Kullanıcı bulunamadı." : "No users found."}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && tab === "tickets" && (
        <div className="space-y-3" data-testid="admin-tickets">
          {tickets.length === 0 && <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 text-center text-sm text-muted-foreground">{lang === "tr" ? "Destek talebi yok." : "No tickets."}</div>}
          {tickets.map((t, i) => (
            <div key={tid(t) || i} className="panel p-4" data-testid={`admin-ticket-${i}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">{S(t?.subject)}</p>
                  <p className="text-xs text-muted-foreground">{S(t?.category)} · {S(t?.user_email ?? t?.email)}</p>
                </div>
                <select defaultValue={S(t?.status) || "OPEN"} onChange={(e) => setTicketStatus(t, e.target.value)} className="rounded-lg border border-white/10 bg-navy-900 px-2 py-1 text-xs text-foreground">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {replyFor === tid(t) ? (
                <div className="mt-3 flex gap-2">
                  <input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={lang === "tr" ? "Yanıt…" : "Reply…"} className="flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground outline-none" />
                  <button onClick={() => sendReply(t)} className="inline-flex items-center gap-1.5 rounded-xl bg-atlas-500 px-3 py-2 text-sm font-bold text-atlas-ink"><Send className="h-4 w-4" /></button>
                </div>
              ) : (
                <button onClick={() => { setReplyFor(tid(t)); setReplyText(""); }} className="mt-3 text-xs font-semibold text-atlas-300">{lang === "tr" ? "Yanıtla" : "Reply"}</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
